import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw, X } from "lucide-react";
import { api, type GraphicElement } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type Props = {
  open: boolean;
  onClose: () => void;
  kind: string;
  familyId?: string;
  currentId?: string;
  seedTags?: string[];
  onPick: (el: GraphicElement) => void;
};

export default function ElementSwapDrawer({
  open,
  onClose,
  kind,
  familyId = "reka-1",
  currentId,
  seedTags = [],
  onPick,
}: Props) {
  const qc = useQueryClient();
  const [tags, setTags] = useState<string[]>(seedTags.slice(0, 3));
  const [tagsMode, setTagsMode] = useState<"and" | "or">("and");
  const [brief, setBrief] = useState("");
  const metaQ = useQuery({ queryKey: ["elements-meta"], queryFn: () => api.elementsMeta() });
  const listQ = useQuery({
    queryKey: ["elements-swap", kind, familyId, tags.join(","), tagsMode],
    queryFn: () =>
      api.elementsList({
        kind,
        family: familyId,
        status: "approved",
        tags: tags.join(","),
        tags_mode: tagsMode,
        limit: 48,
      }),
    enabled: open && Boolean(kind),
  });
  const genM = useMutation({
    mutationFn: () =>
      api.elementsGenerate({
        kind,
        brief: brief || `podobny ${kind}`,
        tags_wanted: tags,
        family_id: familyId,
        count: 1,
        engine: "auto",
        status: "approved",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["elements-swap"] });
      qc.invalidateQueries({ queryKey: ["elements"] });
    },
  });
  const taxonomy = metaQ.data?.taxonomy?.tags || [];
  const items = listQ.data?.items || [];
  const chipTags = useMemo(() => taxonomy.slice(0, 16), [taxonomy]);

  if (!open) return null;

  return (
    <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l border-border bg-card shadow-xl">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div>
          <div className="text-sm font-semibold">Podmień element</div>
          <div className="text-xs text-muted-foreground">kind: {kind}</div>
        </div>
        <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Zamknij">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="space-y-3 overflow-y-auto p-4">
        <div className="flex flex-wrap gap-1.5">
          {chipTags.map((t) => {
            const on = tags.includes(t.id);
            return (
              <button
                key={t.id}
                type="button"
                className={cn(
                  "rounded-full border px-2 py-0.5 text-[11px]",
                  on ? "border-brand bg-brand/15 text-brand" : "border-border text-muted-foreground",
                )}
                onClick={() =>
                  setTags((prev) => (on ? prev.filter((x) => x !== t.id) : [...prev, t.id].slice(0, 5)))
                }
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant={tagsMode === "and" ? "default" : "outline"}
            onClick={() => setTagsMode("and")}
          >
            AND
          </Button>
          <Button
            type="button"
            size="sm"
            variant={tagsMode === "or" ? "default" : "outline"}
            onClick={() => setTagsMode("or")}
          >
            OR
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => listQ.refetch()}>
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {listQ.isLoading ? (
            <div className="col-span-3 flex justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="col-span-3 text-sm text-muted-foreground">Brak approved w tym filtrze.</div>
          ) : (
            items.map((el) => (
              <button
                key={el.id}
                type="button"
                className={cn(
                  "rounded-lg border bg-[#f8f5ef] p-1",
                  el.id === currentId ? "border-brand ring-1 ring-brand" : "border-border",
                )}
                onClick={() => {
                  onPick(el);
                  onClose();
                }}
              >
                <img
                  src={api.elementsPng(el.id, true)}
                  alt={el.short || el.id}
                  className="aspect-square w-full object-contain"
                />
                <div className="truncate px-0.5 pt-1 text-[10px] text-muted-foreground">{el.short || el.id}</div>
              </button>
            ))
          )}
        </div>
        <div className="space-y-2 border-t border-border pt-3">
          <div className="text-xs font-medium">Zamów podobny</div>
          <Input value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="brief (opcjonalnie)" />
          <Button type="button" className="w-full" disabled={genM.isPending} onClick={() => genM.mutate()}>
            {genM.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Generuj 1 kandydat
          </Button>
        </div>
      </div>
    </aside>
  );
}
